import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useAuthStore } from '../../store/authStore';
import { loginRequest } from '../../config/authConfig';

interface ProtectedRouteProps {
  children: ReactNode;
}

const inProgressLabel: Record<string, string> = {
  [InteractionStatus.None]: 'None',
  [InteractionStatus.Startup]: 'Startup',
  [InteractionStatus.HandleRedirect]: 'HandleRedirect',
  [InteractionStatus.AcquireToken]: 'AcquireToken',
  [InteractionStatus.Logout]: 'Logout',
};

/**
 * Protected route wrapper component.
 * Waits for MSAL redirect handling (B2C callback) before redirecting.
 * When not authenticated, triggers B2C login redirect instead of a login page.
 */
export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();
  const { instance, inProgress, accounts } = useMsal();

  const inProgressStr = inProgressLabel[inProgress] ?? String(inProgress);
  if (import.meta.env.DEV) {
    console.log('[ProtectedRoute] render', {
      path: location.pathname,
      inProgress: inProgressStr,
      accountsCount: accounts.length,
      isAuthenticated,
    });
  }

  // MSAL is still processing (e.g. handling redirect after B2C sign-in)
  if (inProgress !== InteractionStatus.None) {
    if (import.meta.env.DEV) console.log('[ProtectedRoute] → show loading (MSAL inProgress !== None)');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // We have MSAL accounts but auth store not set yet – SsoAuthHandler is still running
  if (accounts.length > 0 && !isAuthenticated) {
    if (import.meta.env.DEV) console.log('[ProtectedRoute] → show loading (accounts present, auth store not set yet – waiting for SsoAuthHandler)');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    if (import.meta.env.DEV) console.log('[ProtectedRoute] → redirect to B2C (not authenticated)');
    instance.loginRedirect(loginRequest).catch(console.error);
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (import.meta.env.DEV) console.log('[ProtectedRoute] → render children (allowed)');
  return <>{children}</>;
};
